import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  Clock, CheckCircle2, AlertTriangle, AlertCircle, Calendar, Users, 
  FileText, Activity, RefreshCw, Plus, ChevronRight, Bell, Home,
  Stethoscope, Heart, BookHeart, ClipboardList, UserCheck, Play, Mic,
  Target, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import VisitTemplateForm from "../components/VisitTemplateForm";
import AdmissionGuidance from "../components/AdmissionGuidance";

export default function AdmissionWorkflowTracker() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [workflows, setWorkflows] = useState([]);
  const [summary, setSummary] = useState({ total_active: 0, compliant: 0, at_risk: 0, non_compliant: 0 });
  const [pendingTasks, setPendingTasks] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showNewWorkflow, setShowNewWorkflow] = useState(false);
  const [templateConfig, setTemplateConfig] = useState(null);

  const allowedRoles = ["nurse", "rn", "lpn", "staff", "coordinator", "admin", "administrator", "sw", "chaplain"];

  useEffect(() => {
    if (authLoading) return;
    if (!user || !allowedRoles.includes(user?.role?.toLowerCase())) {
      toast.error("Access denied - Staff access required");
      navigate("/");
      return;
    }
    loadAllData();
  }, [user, authLoading, navigate]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchDashboard(), fetchPendingTasks(), fetchAlerts()]);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get("/admission-workflow/dashboard");
      setWorkflows(response.data.workflows || []);
      setSummary(response.data.summary || { total_active: 0, compliant: 0, at_risk: 0, non_compliant: 0 });
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  };

  const fetchPendingTasks = async () => {
    try {
      const response = await api.get("/admission-workflow/tasks/pending");
      setPendingTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get("/admission-workflow/alerts");
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  };

  const fetchWorkflowDetails = async (patientId) => {
    try {
      const response = await api.get(`/admission-workflow/patient/${patientId}`);
      setSelectedWorkflow(response.data);
    } catch (error) {
      console.error("Error fetching workflow:", error);
    }
  };

  const completeTask = async (taskId) => {
    try {
      const response = await api.put(`/admission-workflow/tasks/${taskId}/complete`);
      toast.success(`Task completed ${response.data.compliance_status === "ok" ? "on time" : "late"}`);
      fetchPendingTasks();
      fetchDashboard();
      if (selectedWorkflow) {
        fetchWorkflowDetails(selectedWorkflow.workflow?.patient_id);
      }
    } catch (error) {
      toast.error("Failed to complete task");
    }
  };

  const getComplianceColor = (status) => {
    switch (status) {
      case "compliant": return "bg-green-500";
      case "at_risk": return "bg-yellow-500";
      case "non_compliant": return "bg-red-500";
      case "overdue": return "bg-red-600";
      case "critical": return "bg-red-500";
      case "warning": return "bg-yellow-500";
      case "ok": return "bg-green-500";
      default: return "bg-gray-500";
    }
  };

  const getComplianceBadge = (status) => {
    switch (status) {
      case "compliant":
      case "ok":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Compliant</Badge>;
      case "at_risk":
      case "warning":
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">At Risk</Badge>;
      case "non_compliant":
      case "overdue":
      case "critical":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">{status === "overdue" ? "Overdue" : "Non-Compliant"}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTaskIcon = (taskType) => {
    switch (taskType) {
      case "admission_visit": return <Home className="h-4 w-4" />;
      case "comprehensive_visit": return <Stethoscope className="h-4 w-4" />;
      case "sw_assessment": return <Heart className="h-4 w-4" />;
      case "chaplain_assessment": return <BookHeart className="h-4 w-4" />;
      case "bereavement_contact": return <Users className="h-4 w-4" />;
      case "initial_poc": return <ClipboardList className="h-4 w-4" />;
      case "idg_review": return <UserCheck className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const formatDeadline = (deadline) => {
    const date = new Date(deadline);
    const now = new Date();
    const hours = Math.round((date - now) / (1000 * 60 * 60));
    
    if (hours < 0) return `${Math.abs(hours)}h overdue`;
    if (hours < 24) return `${hours}h remaining`;
    return `${Math.round(hours / 24)}d remaining`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white" data-testid="admission-workflow-tracker">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 py-6 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Activity className="h-7 w-7" />
              Admission Workflow Tracker
            </h1>
            <p className="text-teal-100 mt-1 text-sm">
              Medicare-compliant admission workflows with deadline management
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              onClick={() => { fetchDashboard(); fetchPendingTasks(); fetchAlerts(); }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button 
              className="bg-white text-teal-700 hover:bg-teal-50"
              onClick={() => setShowNewWorkflow(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              New Admission
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Active Workflows</p>
                  <p className="text-3xl font-bold text-white">{summary.total_active}</p>
                </div>
                <div className="h-12 w-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Activity className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Compliant</p>
                  <p className="text-3xl font-bold text-green-400">{summary.compliant}</p>
                </div>
                <div className="h-12 w-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">At Risk</p>
                  <p className="text-3xl font-bold text-yellow-400">{summary.at_risk}</p>
                </div>
                <div className="h-12 w-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Non-Compliant</p>
                  <p className="text-3xl font-bold text-red-400">{summary.non_compliant}</p>
                </div>
                <div className="h-12 w-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts Section */}
        {alerts.length > 0 && (
          <Card className="bg-red-900/20 border-red-500/30 mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-red-400 flex items-center gap-2 text-lg">
                <Bell className="h-5 w-5" />
                Active Alerts ({alerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {alerts.slice(0, 5).map((alert, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {alert.severity === "critical" ? (
                        <AlertCircle className="h-5 w-5 text-red-400" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-yellow-400" />
                      )}
                      <div>
                        <p className="text-white font-medium">{alert.title}</p>
                        <p className="text-slate-400 text-sm">{alert.message}</p>
                      </div>
                    </div>
                    {alert.action_url && (
                      <Button size="sm" variant="outline" className="text-teal-400 border-teal-500/30">
                        View
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-slate-800/50 border border-slate-700">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-teal-600">
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="guidance" className="data-[state=active]:bg-teal-600">
              <Target className="h-4 w-4 mr-1" />
              Guided Workflow
            </TabsTrigger>
            <TabsTrigger value="tasks" className="data-[state=active]:bg-teal-600">
              Pending Tasks ({pendingTasks.length})
            </TabsTrigger>
            <TabsTrigger value="workflows" className="data-[state=active]:bg-teal-600">
              All Workflows
            </TabsTrigger>
            <TabsTrigger value="requirements" className="data-[state=active]:bg-teal-600">
              Medicare Requirements
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            {/* Quick Guidance Card - Compact view */}
            {workflows.length > 0 && (
              <Card className="bg-gradient-to-r from-teal-900/40 to-emerald-900/40 border-teal-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-teal-500/20 rounded-lg">
                        <Sparkles className="h-5 w-5 text-teal-400" />
                      </div>
                      <div>
                        <p className="text-teal-300 font-medium">Guided Workflow Available</p>
                        <p className="text-sm text-slate-400">Step-by-step guidance for admission compliance</p>
                      </div>
                    </div>
                    <Button 
                      className="bg-teal-600 hover:bg-teal-700"
                      onClick={() => setActiveTab("guidance")}
                    >
                      <Target className="h-4 w-4 mr-2" />
                      Open Guidance
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pending Tasks Preview */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="h-5 w-5 text-teal-400" />
                    Upcoming Deadlines
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Tasks requiring attention
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingTasks.slice(0, 5).map((task) => (
                      <div key={task.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${getComplianceColor(task.compliance_status)}/20`}>
                            {getTaskIcon(task.task_type)}
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm">{task.title}</p>
                            <p className="text-slate-400 text-xs">{task.patient_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {getComplianceBadge(task.compliance_status)}
                          <p className="text-slate-400 text-xs mt-1">{formatDeadline(task.deadline)}</p>
                        </div>
                      </div>
                    ))}
                    {pendingTasks.length === 0 && (
                      <p className="text-slate-400 text-center py-4">No pending tasks</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Active Workflows Preview */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="h-5 w-5 text-teal-400" />
                    Active Admissions
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Patients in admission workflow
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {workflows.slice(0, 5).map((wf) => (
                      <div 
                        key={wf.id} 
                        className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg cursor-pointer hover:bg-slate-900/70"
                        onClick={() => fetchWorkflowDetails(wf.patient_id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`h-3 w-3 rounded-full ${getComplianceColor(wf.overall_compliance)}`}></div>
                          <div>
                            <p className="text-white font-medium text-sm">{wf.patient_name}</p>
                            <p className="text-slate-400 text-xs">Admitted {formatDate(wf.admission_date)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress 
                            value={(wf.tasks_completed / wf.tasks_total) * 100} 
                            className="w-20 h-2"
                          />
                          <span className="text-slate-400 text-xs">{wf.tasks_completed}/{wf.tasks_total}</span>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    ))}
                    {workflows.length === 0 && (
                      <p className="text-slate-400 text-center py-4">No active admissions</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Medicare Timeline Reference */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-teal-400" />
                  Admission Timeline Requirements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-400 font-bold text-lg">48 hours</p>
                    <p className="text-white text-sm">RN Admission Visit</p>
                    <p className="text-slate-400 text-xs mt-1">From admission date</p>
                  </div>
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <p className="text-purple-400 font-bold text-lg">48 hours</p>
                    <p className="text-white text-sm">Comprehensive/SFV</p>
                    <p className="text-slate-400 text-xs mt-1">If moderate-severe symptoms</p>
                  </div>
                  <div className="p-4 bg-teal-500/10 border border-teal-500/30 rounded-lg">
                    <p className="text-teal-400 font-bold text-lg">5 days</p>
                    <p className="text-white text-sm">IDG Assessments & PoC</p>
                    <p className="text-slate-400 text-xs mt-1">SW, Chaplain, Bereavement</p>
                  </div>
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-400 font-bold text-lg">14 days</p>
                    <p className="text-white text-sm">IDG Meeting Cycle</p>
                    <p className="text-slate-400 text-xs mt-1">Review & update care plan</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Guided Workflow Tab */}
          <TabsContent value="guidance" className="space-y-4">
            {workflows.length > 0 ? (
              <div className="space-y-6">
                {/* Workflow selector if multiple */}
                {workflows.length > 1 && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-white text-base">Select Patient Workflow</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {workflows.map((wf) => (
                          <Button
                            key={wf.id}
                            variant={selectedWorkflow?.workflow?.id === wf.id ? "default" : "outline"}
                            className={selectedWorkflow?.workflow?.id === wf.id ? "bg-teal-600" : ""}
                            onClick={() => fetchWorkflowDetails(wf.patient_id)}
                          >
                            {wf.patient_name}
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Auto-select first workflow if none selected */}
                {!selectedWorkflow && workflows.length > 0 && (
                  <div className="text-center py-8">
                    <p className="text-slate-400 mb-4">Select a patient to view their guided workflow</p>
                    <Button 
                      className="bg-teal-600 hover:bg-teal-700"
                      onClick={() => fetchWorkflowDetails(workflows[0].patient_id)}
                    >
                      View {workflows[0].patient_name}'s Workflow
                    </Button>
                  </div>
                )}
                
                {/* Admission Guidance Component */}
                {selectedWorkflow && (
                  <AdmissionGuidance
                    workflow={selectedWorkflow.workflow}
                    tasks={selectedWorkflow.tasks || []}
                    userRole={user?.role?.toLowerCase() || "rn"}
                    onLaunchTemplate={(config) => {
                      setTemplateConfig(config);
                      setSelectedTask({
                        id: config.taskId,
                        patient_id: config.patientId,
                        patient_name: config.patientName,
                        task_type: config.visitType
                      });
                      setShowTemplateForm(true);
                    }}
                    onComplete={async (taskId) => {
                      await completeTask(taskId);
                      fetchWorkflowDetails(selectedWorkflow.workflow.patient_id);
                    }}
                  />
                )}
              </div>
            ) : (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="py-12 text-center">
                  <Target className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                  <h3 className="text-lg text-white font-medium mb-2">No Active Admissions</h3>
                  <p className="text-slate-400 mb-4">Start a new admission workflow to use guided documentation</p>
                  <Button 
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={() => setShowNewWorkflow(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Admission
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Pending Tasks Tab */}
          <TabsContent value="tasks" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">All Pending Tasks</CardTitle>
                <CardDescription className="text-slate-400">
                  Sorted by deadline urgency
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingTasks.map((task) => (
                    <div key={task.id} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${getComplianceColor(task.compliance_status)}/20`}>
                          {getTaskIcon(task.task_type)}
                        </div>
                        <div>
                          <p className="text-white font-medium">{task.title}</p>
                          <p className="text-slate-400 text-sm">{task.patient_name}</p>
                          <p className="text-slate-500 text-xs mt-1">{task.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          {getComplianceBadge(task.compliance_status)}
                          <p className="text-slate-400 text-sm mt-1">
                            {task.hours_remaining !== undefined && (
                              task.hours_remaining < 0 
                                ? `${Math.abs(task.hours_remaining)}h overdue`
                                : `${task.hours_remaining}h remaining`
                            )}
                          </p>
                          <p className="text-slate-500 text-xs">Due: {formatDate(task.deadline)}</p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => {
                              setSelectedTask(task);
                              setShowTemplateForm(true);
                            }}
                            data-testid={`document-task-${task.id}`}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Document
                          </Button>
                          <Badge variant="outline" className="text-xs text-slate-400 justify-center">
                            <Mic className="h-3 w-3 mr-1" />
                            Speech-to-Text
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                  {pendingTasks.length === 0 && (
                    <p className="text-slate-400 text-center py-8">All tasks completed! 🎉</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Workflows Tab */}
          <TabsContent value="workflows" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">All Admission Workflows</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {workflows.map((wf) => (
                    <div 
                      key={wf.id} 
                      className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 cursor-pointer hover:border-teal-500/50"
                      onClick={() => fetchWorkflowDetails(wf.patient_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`h-4 w-4 rounded-full ${getComplianceColor(wf.overall_compliance)}`}></div>
                          <div>
                            <p className="text-white font-medium">{wf.patient_name}</p>
                            <p className="text-slate-400 text-sm">Admitted: {formatDate(wf.admission_date)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-slate-400 text-xs">Progress</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Progress value={(wf.tasks_completed / wf.tasks_total) * 100} className="w-24 h-2" />
                              <span className="text-white text-sm">{wf.tasks_completed}/{wf.tasks_total}</span>
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-slate-400 text-xs">Benefit Period</p>
                            <p className="text-white font-medium">BP{wf.current_benefit_period}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-slate-400 text-xs">Status</p>
                            {getComplianceBadge(wf.overall_compliance)}
                          </div>
                          <ChevronRight className="h-5 w-5 text-slate-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                  {workflows.length === 0 && (
                    <p className="text-slate-400 text-center py-8">No admission workflows found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Medicare Requirements Tab */}
          <TabsContent value="requirements" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Admission Requirements</CardTitle>
                  <CardDescription className="text-slate-400">
                    Medicare CoP deadlines from admission date
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <Home className="h-5 w-5 text-blue-400" />
                      <p className="text-white font-medium">RN Admission Visit</p>
                    </div>
                    <p className="text-slate-300 text-sm">Within 48 hours of admission</p>
                    <p className="text-slate-400 text-xs mt-2">Comprehensive assessment, comfort kit education, medication review</p>
                  </div>
                  
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <Stethoscope className="h-5 w-5 text-purple-400" />
                      <p className="text-white font-medium">Comprehensive/SFV Visit</p>
                    </div>
                    <p className="text-slate-300 text-sm">Within 48 hours of admission visit (if symptoms moderate-severe)</p>
                    <p className="text-slate-400 text-xs mt-2">Symptom Focused Visit with full physical assessment</p>
                  </div>

                  <div className="p-4 bg-teal-500/10 border border-teal-500/30 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <Users className="h-5 w-5 text-teal-400" />
                      <p className="text-white font-medium">IDG Assessments</p>
                    </div>
                    <p className="text-slate-300 text-sm">Within 5 days of admission</p>
                    <ul className="text-slate-400 text-xs mt-2 space-y-1">
                      <li>• Social Worker assessment</li>
                      <li>• Chaplain/Spiritual Care assessment</li>
                      <li>• Bereavement coordinator contact</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <ClipboardList className="h-5 w-5 text-amber-400" />
                      <p className="text-white font-medium">Initial Plan of Care</p>
                    </div>
                    <p className="text-slate-300 text-sm">Within 5 days of admission</p>
                    <p className="text-slate-400 text-xs mt-2">Interdisciplinary care plan with all IDG signatures</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Benefit Period & Recertification</CardTitle>
                  <CardDescription className="text-slate-400">
                    Medicare certification requirements
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400 font-bold text-lg">90-90-60 Structure</p>
                    <ul className="text-slate-300 text-sm mt-2 space-y-1">
                      <li>• BP1: 90 days (initial)</li>
                      <li>• BP2: 90 days</li>
                      <li>• BP3+: 60 days each</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-400 font-bold">Face-to-Face (F2F) Encounter</p>
                    <p className="text-slate-300 text-sm mt-2">Can be done <strong>30 days before</strong> benefit period end date</p>
                    <p className="text-slate-400 text-xs mt-2">Per 42 CFR 418.22 - Must document terminal prognosis of 6 months or less</p>
                  </div>

                  <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="text-orange-400 font-bold">Recertification</p>
                    <p className="text-slate-300 text-sm mt-2">Can ONLY be done <strong>15 days before</strong> benefit period end date</p>
                    <p className="text-slate-400 text-xs mt-2">Physician certification that patient remains terminally ill</p>
                  </div>

                  <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar className="h-5 w-5 text-cyan-400" />
                      <p className="text-white font-medium">IDG Meetings</p>
                    </div>
                    <p className="text-slate-300 text-sm">Every 14 days</p>
                    <p className="text-slate-400 text-xs mt-2">Review and update plan of care, all disciplines participate</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Workflow Detail Modal */}
        {selectedWorkflow && selectedWorkflow.workflow && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="bg-slate-800 border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">{selectedWorkflow.workflow.patient_name}</CardTitle>
                    <CardDescription className="text-slate-400">
                      Admission Workflow - BP{selectedWorkflow.workflow.current_benefit_period}
                    </CardDescription>
                  </div>
                  <Button variant="ghost" onClick={() => setSelectedWorkflow(null)}>✕</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Workflow Progress */}
                <div className="p-4 bg-slate-900/50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-white font-medium">Workflow Progress</p>
                    <span className="text-teal-400">
                      {selectedWorkflow.workflow.tasks_completed}/{selectedWorkflow.workflow.tasks_total} tasks
                    </span>
                  </div>
                  <Progress 
                    value={(selectedWorkflow.workflow.tasks_completed / selectedWorkflow.workflow.tasks_total) * 100}
                    className="h-3"
                  />
                </div>

                {/* Key Dates */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <p className="text-slate-400 text-xs">Admission Date</p>
                    <p className="text-white text-sm">{formatDate(selectedWorkflow.workflow.admission_date)}</p>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <p className="text-slate-400 text-xs">BP End Date</p>
                    <p className="text-white text-sm">{formatDate(selectedWorkflow.workflow.benefit_period_end)}</p>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <p className="text-slate-400 text-xs">F2F Window Opens</p>
                    <p className="text-white text-sm">{formatDate(selectedWorkflow.workflow.f2f_window_start)}</p>
                  </div>
                  <div className="p-3 bg-slate-900/50 rounded-lg">
                    <p className="text-slate-400 text-xs">Recert Window Opens</p>
                    <p className="text-white text-sm">{formatDate(selectedWorkflow.workflow.recert_window_start)}</p>
                  </div>
                </div>

                {/* Tasks */}
                <div>
                  <p className="text-white font-medium mb-3">Tasks</p>
                  <div className="space-y-2">
                    {selectedWorkflow.tasks?.map((task) => (
                      <div key={task.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {task.status === "completed" ? (
                            <CheckCircle2 className="h-5 w-5 text-green-400" />
                          ) : (
                            <div className={`h-5 w-5 rounded-full border-2 ${
                              task.compliance_status === "overdue" ? "border-red-500" :
                              task.compliance_status === "critical" ? "border-orange-500" :
                              task.compliance_status === "warning" ? "border-yellow-500" :
                              "border-slate-500"
                            }`}></div>
                          )}
                          <div>
                            <p className="text-white text-sm">{task.title}</p>
                            <p className="text-slate-400 text-xs">{task.assigned_role.toUpperCase()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getComplianceBadge(task.compliance_status)}
                          {task.status !== "completed" && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-teal-400 border-teal-500/30"
                              onClick={() => completeTask(task.id)}
                            >
                              Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Visit Template Form Dialog */}
      <Dialog open={showTemplateForm} onOpenChange={setShowTemplateForm}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-400" />
              {selectedTask?.title || "Complete Documentation"}
            </DialogTitle>
            <DialogDescription>
              {selectedTask?.patient_name && `Patient: ${selectedTask.patient_name}`}
              {" • "}LCD-compliant documentation with speech-to-text
            </DialogDescription>
          </DialogHeader>
          {selectedTask && (
            <VisitTemplateForm
              patientId={selectedTask.patient_id}
              patientName={selectedTask.patient_name}
              clinicianId={user?.id}
              initialDiscipline={getDisciplineFromTaskType(selectedTask.task_type)}
              initialVisitType={getVisitTypeFromTaskType(selectedTask.task_type)}
              onSave={async (result) => {
                try {
                  // Mark task as complete
                  await api.put(`/admission-workflow/task/${selectedTask.id}/complete`, {
                    completed_by_id: user?.id,
                    documentation_id: result.visit_id,
                    notes: `Documentation completed with ${result.validation?.completeness_score}% completeness`
                  });
                  toast.success("Task completed with LCD-compliant documentation!");
                  setShowTemplateForm(false);
                  setSelectedTask(null);
                  loadAllData();
                } catch (err) {
                  console.error("Failed to complete task:", err);
                  toast.error("Documentation saved but task update failed");
                }
              }}
              onCancel={() => {
                setShowTemplateForm(false);
                setSelectedTask(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  // Helper function to map task type to discipline
  function getDisciplineFromTaskType(taskType) {
    const mapping = {
      admission_visit: "rn",
      comprehensive_visit: "rn",
      symptom_focused_visit: "rn",
      routine_visit: "rn",
      prn_visit: "rn",
      sw_assessment: "sw",
      chaplain_assessment: "chaplain",
      bereavement_contact: "sw",
      aide_visit: "aide",
      pt_visit: "pt",
      ot_visit: "ot",
      slp_visit: "slp",
      physician_visit: "md",
      np_visit: "np",
      face_to_face: "md",
      recertification: "md",
    };
    return mapping[taskType?.toLowerCase()] || "rn";
  }

  // Helper function to map task type to visit type
  function getVisitTypeFromTaskType(taskType) {
    const mapping = {
      admission_visit: "initial",
      comprehensive_visit: "initial",
      symptom_focused_visit: "initial",
      sw_assessment: "initial",
      chaplain_assessment: "initial",
      bereavement_contact: "initial",
      routine_visit: "routine",
      prn_visit: "prn",
      aide_visit: "routine",
      pt_visit: "initial",
      ot_visit: "initial",
      slp_visit: "initial",
      face_to_face: "f2f",
      recertification: "recert",
    };
    return mapping[taskType?.toLowerCase()] || "routine";
  }
}
